import {
  GraduationCap,
  Laptop,
  BadgePercent,
  BookOpenText,
  BookUser,
  Boxes,
  FileInput,
  CarFront,
  ChartColumnIncreasing,
  KeyRound,
  Receipt,
  Route,
  Wallet,
  Wrench,
  Building2,
  CalendarDays,
  ClipboardList,
  DoorOpen,
  FilePen,
  FileSpreadsheet,
  FileText,
  FileType2,
  FolderTree,
  IdCard,
  LayoutDashboard,
  Megaphone,
  MessageSquare,
  MessageSquareText,
  Network,
  Package,
  PackagePlus,
  PackageSearch,
  Percent,
  PieChart,
  Presentation,
  ReceiptText,
  ScanLine,
  ScrollText,
  Server,
  Settings2,
  ShoppingCart,
  Stethoscope,
  StickyNote,
  Truck,
  Users,
  UsersRound,
  ClipboardCheck,
  HandCoins,
  Banknote,
  ListOrdered,
  BookText,
  Scale,
  ListTree,
  Calculator,
  Workflow,
  ArrowLeftRight,
  Cable,
  FileChartColumn,
  Target,
  ChartColumnBig,
  GitCompareArrows,
  CalendarCheck,
  Landmark,
} from "lucide-react";

import type { NavGroup, NavItem, NavPanelOption } from "@altek/ui/models/layout/nav-catalog";

/**
 * The application's panels, and the order the rail offers them in.
 *
 * THIS UNION USED TO LIVE IN THE KIT and that was the wrong place: adding a screen to the
 * product meant widening a type in a LIBRARY and cutting a release of it. The catalogue below
 * moved here with it — the data changes when the product changes, so it belongs to the product.
 *
 * Order is widest audience first: everyone has a workspace, most people have an employee
 * panel, and the business panels narrow from there.
 */
export type NavPanel =
  | "workspace"
  | "hr"
  | "inventory"
  | "operations"
  | "sales"
  | "accounting"
  | "finance"
  | "marketing"
  | "fleet"
  | "reports"
  | "admin"
  | "platform";

/**
 * Label travels with the id because the two are one decision. A separate `Record` of labels
 * was a second place to edit when a panel was added, and a missing entry printed `undefined`
 * under somebody's name in the rail.
 */
export const NAV_PANELS: readonly NavPanelOption<NavPanel>[] = [
  // The first four keep the wording the product already used; the panel name appears under a
  // person's own name in the rail, and renaming somebody's role as a side effect of moving a
  // file is not a change anybody asked for.
  { id: "workspace", label: "Çalışma Alanı" },
  { id: "hr", label: "İK" },
  { id: "inventory", label: "Stok" },
  /**
   * Operasyon (17 Ekim 2026): sevkiyat işleri ve kapı. `module.operations` kendi alanı ve kendi
   * türetilmiş bölüm rolü var (`OPERATIONS_YONETICI`); stokun altına konsaydı stok görüntüleyicisi
   * bölüm başlığını taşıyıp satırları göremezdi. Panel üç yerde: bu birleşim, bu liste, grup.
   */
  { id: "operations", label: "Operasyon" },
  { id: "sales", label: "Satış" },
  /**
   * Etkinlik KENDİ paneli, satışın bir kutusu değil — `module.events` `AREA_MODULES`'ta
   * olduğu için alanı da kendi (`check:role-areas`). Kongre bütçesi, hekim daveti ve yıllık
   * plan satışın anahtarlarıyla değil kendi anahtarlarıyla açılıyor; satışın altına konsaydı
   * `SALES_YONETICI` onu görmeden panelin adını taşırdı.
   *
   * SATIRLARI HENÜZ YOK ve bu kasıtlı: menü satırı ancak rotası varken eklenebiliyor
   * (`route-language.test.ts`, «menüdeki her adres rota tablosunda da vardır»). Ekranlar
   * yazıldığında satırlar buraya gelecek; panelin kimliği önden duruyor ki ekranları yazan
   * oturum birleşim tipine dokunmak zorunda kalmasın.
   */
  { id: "accounting", label: "Muhasebe" },
  { id: "finance", label: "Finans" },
  { id: "marketing", label: "Pazarlama" },
  // Şirket içi filo (6 Ekim 2026): kendi bölümü ve kendi bölüm rolü (`FLEET_YONETICI`).
  { id: "fleet", label: "Filo" },
  /**
   * Raporlar KENDİ alanı, yönetimin bir kutusu değil.
   *
   * Ölçüldü: `reporting.report.read` anahtarını Patron, İK Müdürü, Müdür ve Depo Müdürü
   * tutuyor — okuyucuları modüllerin ÜSTÜNDE duruyor, hiçbirinin içinde değil. Yönetim
   * panelinde kaldığı sürece sistem yöneticisinin menüsünde iş içeriği görünüyordu (rail
   * `ownArea` ile yönetime sabitleniyor); İK'ya ya da Stok'a koymak ise dört okuyucudan
   * ikisini dışarıda bırakırdı. Kendi alanı, ikisini de yapmayan tek yer.
   *
   * ADI «ANALİZ» (A2, 1 Kasım 2026; `docs/plan/ANALIZ_PLANI.md` §3.2): «Raporlar» rotası 8 Ekim'de ham veri gösterdiği
   * için kapanmıştı ve panel boştu; Analiz bölümü bu panele girdi. Kimlik (`reports`) değişmedi — rayın hatırladığı
   * seçim ve `/reports/exports` bağları kırılmasın; değişen yalnız kişinin gördüğü ad.
   */
  { id: "reports", label: "Analiz" },
  { id: "admin", label: "Yönetim" },
  // Last, and only present on the shared service. It is not a wider "Yönetim" — it is a
  // different audience: the vendor looking at customers, not a customer looking at themselves.
  { id: "platform", label: "Platform" },
];

/**
 * Which shape of installation a group belongs to.
 *
 * WHY THIS IS A FIELD AND NOT A PERMISSION CHECK. The obvious alternative was to let the
 * `platform.*` permission decide: no key, no menu. That reasoning is what made this necessary
 * to write down, because on a DEDICATED installation the customer's own administrator holds
 * every permission the catalogue has — so a permission-only gate would have shown İntraline's
 * IT manager a "Platform" tab listing subscriptions and licence keys. The keys are now kept out
 * of `SYSTEM_ADMIN` in the backend (`VENDOR_PERMS`), which closes the hole; this field closes
 * the QUESTION, by saying the screens do not belong to that shape of installation at all.
 *
 * `undefined` means "both", which is every group but one and is why the field is optional: a
 * required flag on fifteen groups that all answer the same way is a field people stop reading.
 */
export type Installation = "SHARED" | "DEDICATED";

/**
 * Kitin girdisi, «herhangi biri yeterli» seçeneğiyle.
 *
 * Kit `permissions`'ı HEPSİ diye okuyor ve gerekçesi iyi: "şu ya da bu" çoğu zaman yanlışlıkla
 * birleştirilmiş iki ekran demek. Sekmeli BÖLÜM satırı o çoğunluğun dışında kalıyor — sekmeler
 * bilerek tek sayfada ve her birinin kendi anahtarı var. Bölümü HEPSİ ile kapatmak, canlıda
 * «Müdür» ve «Patron» rollerini — Sistem'in dört anahtarından yalnız `workflow.definition.read`
 * tutuyorlar — girebilecekleri bir sekmeden mahrum bırakıyordu.
 *
 * Çözüm kitin kuralını esnetmek değil, ondan ÖNCE karar vermek: `AppLayout` bu alanı kendi
 * çözüyor ve kite yalnız sonucu veriyor. Kit bunu birinci sınıf destekleyene kadar geçerli.
 */
export interface AppNavItem extends NavItem {
  /** Bunlardan HERHANGİ biri yeterli. `permissions` ile birlikte kullanılmaz. */
  anyPermissions?: readonly string[];
  /**
   * Bunlardan BİRİ varsa satır MENÜDE çizilmiyor (30 Ekim 2026) — aynı adlı iki satırı önlemek için:
   * kişisel «Sunum Talepleri» satırı, pazarlamanın bütün talepler satırını görene çizilmiyor. Yalnız
   * menüyü süzüyor; rotanın kapısı değişmiyor (adres yine açılıyor).
   */
  unlessPermissions?: readonly string[];
  /**
   * Satır KİŞİNİN KENDİ kayıtlarını listeliyor (30 Ekim 2026): grubu «başkalarının kaydı»
   * (`aboutOthers`) olsa da SELF kapsamlı anahtar yetiyor. Kitin filtreyi grubun yüklemini
   * (`canForOthers`) soracağı için anahtar burada `can` ile çözülüp kite anahtarsız veriliyor;
   * rota da `requireAnyRecord` almıyor (`router.tsx › NAV_ITEMS`).
   */
  ownRecords?: true;
}

export interface AppNavGroup extends Omit<NavGroup<NavPanel>, "items"> {
  /** The installation shape this group appears in. Omit for both. */
  installation?: Installation;
  /**
   * Kayıtlar şirket düzeyinde, birim sütunu yok (3 Kasım 2026, filo): anahtarı birim kapsamıyla taşıyan direktöre sunucu boş liste
   * dönüyor (`legalEntityReach`), satır da boş ekrana götürürdü. Menüde çizilmiyor; rotanın kapısı değişmiyor.
   */
  companyLevel?: true;
  items: readonly AppNavItem[];
}

/** Menünün sorduğu üç soru. `scopeOf` yalnız `companyLevel` grubu için; el yapımı sınama nesnesinde yoksa şirkete ulaşıyor sayılır. */
export interface NavHolder {
  can: (key: string) => boolean;
  canForOthers: (key: string) => boolean;
  scopeOf?: (key: never) => { instanceWide: boolean; legalEntityIds: readonly string[] } | null | undefined;
}

/**
 * Panels whose items are always offered, whatever the rail is narrowed to.
 *
 * WHAT THE NARROWING IS FOR. An administrator's rail is narrowed to the administration, because
 * a rail built honestly from what `SYSTEM_ADMIN` may open contains every employee and İK screen
 * too — thirty rows they did not come for, most of which do not work for an account with no
 * employee record behind it. The exceptions below are the panels that narrowing gets WRONG.
 *
 * `workspace` — the personal toolbox is not a module. An administrator still has notes and a
 * chat, and dropping them was the "modüller gelmemiş" report.
 *
 * `platform` — the vendor's own area, and it very nearly shipped missing. The keys were held,
 * the catalogue had the group, the dashboard offered the shortcuts, and the RAIL had nothing:
 * the screens were reachable only by typing the URL. The reasoning is the same shape as the
 * workspace's — the narrowing hides areas an administrator can see only BECAUSE they hold every
 * permission, and this is not one of those. Nobody holds `platform.*` by accident: the keys are
 * kept out of every customer role template and granted one account at a time from a script.
 *
 * NEITHER ENTRY GRANTS ANYTHING. The area is filtered AFTER `visibleNav` has resolved each row
 * against the viewer's own permissions, so a customer's own administrator — who holds every key
 * except those four — sees no trace of the platform panel here.
 */
export const ALWAYS_VISIBLE_PANELS: readonly NavPanel[] = ["workspace", "platform"];

/**
 * Kişinin İŞ ALANINI adlandıran paneller — kitin `areaLabelFor`'u (listeyi tersten tarayıp kişinin ulaştığı en dar
 * alanı döndürüyor) bunu almalı, `NAV_PANELS`'i değil. Analiz (A2, 1 Kasım 2026) alanların üstünde bir araç: İK
 * uzmanı İK sekmesini, depo müdürü stok sekmesini okuyor; listede kalsaydı ikisinin alanı da «Analiz» çıkardı.
 */
export const WORK_AREA_PANELS: readonly NavPanelOption<NavPanel>[] = NAV_PANELS.filter((panel) => panel.id !== "reports");

/**
 * Kenar çubuğundaki şirket satırının YEDEK adı.
 *
 * Gerçek ad oturumdan geliyor; bu yalnız oturum henüz okunmadığında ve testlerde görünen hâli.
 * Tek bir yerde durması, `AppLayout`'un satırı hangi başlıkla bulacağını da belirliyor.
 */
export const COMPANY_GROUP_TITLE = "Şirket";

export const NAV_CATALOG: readonly AppNavGroup[] = [
  /* --------------------------------------------------------------- workspace panel */
  /**
   * The personal toolbox. Every item here is about the signed-in ACCOUNT, so each one works
   * for a system administrator with no employee record just as it does for a warehouse
   * operator — the reason this area survives the administration rail's narrowing.
   *
   * `core.directory` needs no module gate (a core module is always on); the other five are
   * switchable, and a customer who did not buy chat sees no chat row rather than a disabled one.
   */
  {
    title: "Çalışma alanı",
    panel: "workspace",
    aboutOthers: false,
    items: [
      /**
       * «PANELİM» — ÜRÜNDEKİ TEK PANO, ve çalışma alanının ilk satırı.
       *
       * «BENİM İŞLERİM» BÖLÜMÜ KALDIRILDI (16 Eylül 2026, kullanıcı kararı). Beş satırlık bir
       * bölümdü — Panelim, İzin Taleplerim, İzin Hareketlerim, İzin Takvimi, Sağlık Raporlarım
       * — ve dördü aynı şeyin parçalarıydı: kişinin kendi izni. Dört satır, panelin içinde dört
       * düğme oldu; ekranlar ve ADRESLERİ olduğu gibi duruyor, rotaları `EXTRA_ROUTES`'ta KENDİ
       * anahtarlarıyla (`under` ile DEĞİL — bu dosyadaki «Dışa Aktarımlar» dersi: menüden çıkan
       * bir satırın yeri değişir, kapısı değişmez).
       *
       * «İK PANOSU» VE «YÖNETİM PANOSU» DA KALKTI, aynı kararla. İçerikleri kaybolmadı: her
       * departmanın panosu Panelim'in içinde bir BLOK oldu ve bloklar kişinin yetkisine göre
       * açılıyor (`EmployeeDashboardPage`). İK uzmanının rayında iki pano birden duruyordu ve
       * hangisinin «asıl» olduğu belli değildi.
       *
       * ÇALIŞMA ALANINDA, çünkü burası `ALWAYS_VISIBLE_PANELS`'ta: yönetim rayı daraltılırken
       * de hayatta kalan iki panelden biri. Panelim başka bir panelde olsaydı sistem
       * yöneticisinin rayından düşerdi ve `AppLayout` sabitleyecek bir pano bulamazdı.
       *
       * KAPISIZ, ve öyle kalmalı: her oturum açan kişinin bir panosu var. İçindeki her blok
       * kendi kapısını kendi taşıyor — duyuru ve bildirim akışı da 1 Kasım 2026'dan beri
       * (`FeedPanelBlock`): taşımacının şoförü Panelim'i açar, şirketin duyurusunu görmez.
       *
       * BU SATIR RAYDA ÇİZİLMİYOR. `AppLayout` onu `lockedFavouriteIds` ile FAVORİLER'in başına
       * sabitliyor; kit sabitlenmiş satırı kendi bölümünden düşürüyor ve yıldızını çizmiyor.
       * Yani katalogda durması gerekiyor — çıkarılsaydı kısayol da yok olurdu — ama çalışma
       * alanının altında ikinci kez görünmüyor. Tek istisna şoför hesabı: orada sabitlenen
       * «Seferlerim» (`DASHBOARD_HREFS`), Panelim çalışma alanında sıradan bir satır.
       */
      { label: "Panelim", to: "/my-panel", icon: LayoutDashboard },
      /**
       * SEFERLERİM (1 Kasım 2026) — şoförün kendi seferleri; yalnız «Şoför» rolünde (`operations.self.read`,
       * türetilmez, taranmaz). Çalışma alanında, çünkü kişinin KENDİ işi ve bu grup her rolde açık kalıyor:
       * taşımacının şoförünün rayında başka bölüm yok. Şoförün başlangıç ekranı: `AppLayout`'un
       * `DASHBOARD_HREFS`'inde Panelim'den sonra, yani kök adres buraya gidiyor ve satır sabitleniyor.
       */
      {
        label: "Seferlerim",
        to: "/my-trips",
        icon: Route,
        permissions: ["operations.self.read"],
        module: "module.operations",
      },
      /**
       * MASRAFLARIM (finans F2c-1, 2 Kasım 2026; plan §7 «Masraflarım Çalışma alanı bölümünde»): kişinin KENDİ masraf
       * beyanları — liste, yeni beyan, beyan (`/my-expenses/new`, `/my-expenses/:id` `EXTRA_ROUTES`'ta bu satırın
       * kapısıyla; `matchPrefix` ile satır beyanın sayfasında da seçili). Anahtar `finance.expense.submit` her çalışanda
       * (EMPLOYEE); modül `module.finance` kapalıysa satır yok.
       */
      {
        label: "Masraflarım",
        to: "/my-expenses",
        icon: ReceiptText,
        permissions: ["finance.expense.submit"],
        module: "module.finance",
        matchPrefix: true,
      },
      {
        label: "Sohbet",
        to: "/chat",
        icon: MessageSquare,
        permissions: ["chat.conversation.read"],
        module: "module.chat",
        matchPrefix: true,
      },
      {
        label: "Kişi Rehberi",
        to: "/directory",
        icon: BookUser,
        permissions: ["directory.person.read"],
        matchPrefix: true,
      },
      {
        label: "Notlarım",
        to: "/notes",
        icon: StickyNote,
        permissions: ["notes.note.read"],
        module: "module.notes",
        matchPrefix: true,
      },
      {
        label: "Diyagramlarım",
        to: "/diagrams",
        icon: Network,
        permissions: ["diagrams.diagram.read"],
        module: "module.diagrams",
        matchPrefix: true,
      },
      {
        label: "Dosya Dönüştürücü",
        to: "/converter",
        icon: FileType2,
        permissions: ["converter.job.read"],
        module: "module.converter",
        matchPrefix: true,
      },
      {
        /**
         * «BELGELERİM» DEĞİL, ve bu bir zevk meselesi değil: o ad İK'nın özlük dosyasında
         * (`/my-documents`, `hr.document.read`) zaten kullanılıyor. İki satırın aynı adı
         * taşıması, kişinin kendi dolabıyla İK'nın onun hakkında tuttuğu dosyayı aynı şey
         * sanması demekti — ve ikisi gerçekten farklı: birinin sahibi kişi, öbürünün İK.
         *
         * «Dosyalarım», çalışma alanındaki kardeşleriyle de aynı kalıpta: Notlarım,
         * Diyagramlarım, Dosyalarım.
         */
        label: "Dosyalarım",
        to: "/documents",
        icon: FilePen,
        /**
         * İKİ ANAHTARDAN BİRİ YETİYOR. Ekran iki şey yapıyor: kişinin kendi dolabı
         * (`documents.document.read`) ve bir KAYDA bağlı belgeyi düzenleyicide açma yolu
         * (`documents.editor.open`). Yalnız ikincisini taşıyan biri ekrana ulaşamazsa,
         * düzenleyiciye giden anlatıyı da kaybederdi.
         */
        anyPermissions: ["documents.document.read", "documents.editor.open"],
        module: "module.documents",
        matchPrefix: true,
      },
      /**
       * «PROFİLİM» BURADA DEĞİL, KULLANICI MENÜSÜNDE.
       *
       * Kendi profilin bir çalışma aracı değil, hesabının kendisi — ve hesap menüsü sağ üstte,
       * adının altında zaten duruyor ve oraya gidiyor. İki yerde birden durduğunda hangisinin
       * «asıl» olduğu belirsizleşiyor; üstelik menüdeki her fazladan satır, aranan şeyi bir
       * satır aşağı itiyor.
       *
       * Rota (`/profile`) olduğu gibi duruyor.
       */
    ],
  },

  /**
   * «BENİM İŞLERİM» GRUBU MENÜDEN KALDIRILDI — 16 Eylül 2026, kullanıcı kararı.
   *
   * Beş satırı vardı: Panelim, İzin Taleplerim, İzin Hareketlerim, İzin Takvimi, Sağlık
   * Raporlarım. Sonuncu dördü aynı şeyin parçalarıydı — kişinin kendi izni ve kendi belgesi —
   * ve kenar çubuğunda dört satır tutmak, hepsinin zaten panelde özetlendiğini gizliyordu.
   * Panelim ise çalışma alanının ilk satırına taşındı; `panel: "employee"` artık boş kaldığı
   * için birleşim tipinden de düştü.
   *
   * DÖRT EKRAN DURUYOR, YALNIZ YERİ DEĞİŞTİ: rotaları `EXTRA_ROUTES`'ta ve KENDİ anahtarlarıyla
   * (`gate`, `under` DEĞİL). `under: "/my-panel"` yazmak kapıyı büsbütün açardı — Panelim
   * kapısız bir satır, yani izin ekranları da kapısız kalırdı. Bu dosyadaki «Dışa Aktarımlar»
   * dersinin aynısı ve bu kez bedeli daha büyük olurdu.
   *
   * Panele giden yol kaybolmadı: `MyWorkSection` dördünü de düğme olarak taşıyor.
   */

  /**
   * «ONAY VE BİLDİRİM» GRUBU MENÜDEN KALDIRILDI — ekranlar duruyor, yeri değişti.
   *
   * Üçü de her kullanıcıda görünüyordu ve kenar çubuğunun en çok yer kaplayan, en az
   * tıklanan bölümüydü: onay ve bildirim zaten üst çubuktan ulaşılan şeyler, duyuru ise
   * okunup geçilen bir akış. Kenar çubuğu kişinin İŞİNİ taşımalı.
   *
   * Yeni yerleri:
   *   `/approvals`     → üst çubukta düğme, ayrıca kendi panelinde bölüm
   *   `/notifications` → üst çubukta zil (rozetli), ayrıca kendi panelinde bölüm
   *   `/announcements` → panelde son beş duyuru; tam liste YALNIZ oradan açılır
   *
   * Rotaları `EXTRA_ROUTES`'ta, `gate` ile ve KENDİ anahtarlarıyla duruyor. `under:` ile
   * bağlamak yanlış olurdu: aynı dosyadaki «Dışa Aktarımlar» yorumu bunun bedelini ölçüyor —
   * o satır bir kez `under:` ile bağlandı ve kapısı kendi anahtarı yerine bölümün anahtarına
   * düştü, dört rol yetkisi olmadan ekranı açabildi. Menüden çıkan bir satırın kapısı
   * DEĞİŞMEMELİ, yalnız yeri değişmeli.
   */
  {
    title: "İK",
    panel: "hr",
    aboutOthers: true,
    items: [
      /**
       * ON YEDİ SATIR BEŞE İNDİ. Personel(4) + İzin yönetimi(8) + Özlük(4) + Duyuru(1).
       *
       * Yönetim panelinde ve stokta işe yarayan aynı kural: menü BÖLÜME işaret ediyor,
       * sekmeler sayfanın içinde. Otuz satırlık bir menüde insan aradığını okuyarak değil
       * hatırlayarak buluyor.
       *
       * DUYURU SEKMEYE GİRMEDİ: tek ekran ve farklı bir yetki taşıyor
       * (`notifications.announcement.manage`), İK'nın kendi anahtarlarından biri değil.
       *
       * «İK PANOSU» SATIRI DA KALKTI — 16 Eylül 2026, kullanıcı kararı. İK uzmanının rayında
       * iki pano birden duruyordu («Panelim» ve bu) ve ikisi de açılışta bakılan ekrandı; hangisinin
       * «asıl» olduğu belli değildi. Panonun İÇERİĞİ kaybolmadı: Panelim'in İK bloğu oldu
       * (`HrPanelBlock`) ve orada da aynı kapının arkasında duruyor — `hr.employee.read` ya da
       * `hr.leave.read`, ve BAŞKASI için (`canForOthers`), çünkü gösterdiği her satır bir
       * başkasının kaydı. `/hr` adresi ve `HrDashboardPage` ekranı silindi; yerini alan şey bir
       * ekran değil, bir blok.
       */
      {
        label: "Personel",
        to: "/hr/people",
        icon: Users,
        permissions: ["hr.employee.read"],
        module: "module.hr",
        matchPrefix: true,
      },
      /** Adı «İzin ve Puantaj» idi; puantaj 16 Eylül 2026'da kapatılınca içeriğine göre düzeltildi. */
      {
        label: "İzin Yönetimi",
        to: "/hr/leave",
        icon: CalendarDays,
        permissions: ["hr.leave.read"],
        module: "module.hr",
        matchPrefix: true,
      },
      /**
       * «ÖZLÜK DOSYASI» SATIRI KALKTI — ik-personel 6 Ekim 2026 (H9, ÇIKARIM of «Dosya kısmını
       * çıkartacağız»). Belgeyi artık çalışan yüklüyor (H10); İK'nın işi gelen belgeye karar
       * vermek. İçerik kaybolmadı: gelen belgeler, tüm belgeler, süresi yaklaşanlar ve belge
       * türleri Personel › Belgeler sekmesinde (`/hr/people/documents`, `hr.document.read`
       * başkası için); sağlık raporları İzin Yönetimi › Raporlar'da. Eski adresler
       * `router.tsx` `LEGACY_HR_REDIRECTS` ile yeni yerlerine gidiyor.
       */
      /**
       * ZİMMET VE EĞİTİM AYRI SATIR, «Personel»in sekmesi DEĞİL.
       *
       * İkisi de kişi hakkında ama ikisi de kendi başına bir iş: «kimde bilgisayar var»
       * sorusunu soran kişi bir çalışan kartı aramıyor, ve «kim İSG almadı» sorusu bir
       * denetim sorusu. Personel sekmesine gömmek, günlük iki işi bir kartın altına
       * saklardı. Beş satırın yediye çıkmasının sebebi bu — her satır hâlâ ayrı bir iş.
       *
       * Kapıları KENDİ anahtarları: `hr.asset.read` ve `hr.training.read`. Varsayılan
       * rollerde yalnız İK bölümünde var; satış, pazarlama ve depo bu satırları görmüyor.
       */
      {
        label: "Zimmet",
        to: "/hr/assets",
        icon: Laptop,
        permissions: ["hr.asset.read"],
        module: "module.hr",
      },
      /**
       * «ZİMMET TALEPLERİ» — 30 Ekim 2026, kullanıcı: «Talep şimdilik patrona gitsin; sistem yöneticisi ve İK
       * da görsün ya da bildirim alsın.» Çalışanların hasar ve değişim talepleri, onay durumlarıyla. Kapısı
       * KENDİ anahtarı (`hr.asset_request.read`): İK müdürü, patron ve sistem yöneticisi taşıyor; zimmeti
       * okuyan yönetici (`hr.asset.read`) talepleri görmüyor. Kişinin kendi talepleri Panelim › «Zimmetlerim».
       */
      {
        label: "Zimmet talepleri",
        to: "/hr/asset-requests",
        icon: ClipboardList,
        permissions: ["hr.asset_request.read"],
        module: "module.hr",
      },
      /**
       * ÇIKIŞ HESAPLARI — 31 Ekim 2026: ayrılanın hesabı (ücrete dönüşen izin, kıdem ve ihbar tazminatı, ek ödeme,
       * kesinti, net). Kapısı okuma anahtarı (`hr.exit_settlement.read`): İK müdürü, patron ve muhasebenin
       * `EXIT_SETTLEMENT_READER` ek rolü — muhasebe personel kartını açamıyor, hesabı buradan okuyor. Hesabı İK
       * kartın Çıkış sekmesinde giriyor.
       */
      {
        label: "Çıkış hesapları",
        to: "/hr/exit-settlements",
        icon: Wallet,
        permissions: ["hr.exit_settlement.read"],
        module: "module.hr",
      },
      {
        label: "Eğitim ve İSG",
        to: "/hr/training",
        icon: GraduationCap,
        permissions: ["hr.training.read"],
        module: "module.hr",
        matchPrefix: true,
      },
      /**
       * «EKİP GERİ BİLDİRİMLERİ» — İK'DAN ÇIKTI, ÜST YÖNETİME GERİ GELDİ (26 Ekim 2026).
       *
       * İKİ KARAR ÜST ÜSTE. Önce «ekip geri bildirim kısmını şimdilik kaldıralım, İK'dan
       * tamamen kaldıralım, ön yüzde görünmesin» — satır kalktı. Sonra «[geri bildirimin]
       * yöneticiye gitmesi lazım ve patrona, ikisi birden görmesi lazım» — satır geri geldi.
       * ÇELİŞKİ DEĞİL: aynı ekran, başka bir okuyucu. Kapı değişmeden kaldı
       * (`hr.team_feedback.read_all`), KİMDE OLDUĞU değişti — anahtar `HR_MANAGER`'dan alınıp
       * `MANAGER` ve `EXECUTIVE`'e verildi (`seed-permissions.mjs`, `lib/revoked-grants.mjs`).
       * Yani satır artık yönetici ve patronda çıkıyor, İK müdüründe çıkmıyor.
       *
       * BÖLÜM «İK» ÇÜNKÜ KONUSU İNSAN. Yöneticinin kenar çubuğunda İK bölümünün ikinci satırı;
       * İK müdürünün kenar çubuğunda hiç yok. Bölüm başlığı bir SAHİPLİK değil, bir konu.
       */
      {
        label: "Ekip Geri Bildirimleri",
        to: "/hr/team-feedback",
        icon: MessageSquareText,
        permissions: ["hr.team_feedback.read_all"],
        module: "module.hr",
      },
      {
        label: "Duyuru Yönetimi",
        to: "/hr/announcements",
        icon: Megaphone,
        permissions: ["notifications.announcement.manage"],
        module: "module.hr",
      },
    ],
  },

  /**
   * «EKİP» — bir yöneticinin KENDİ ekibi (rol-yetki 21 Ekim 2026, kullanıcı: «ayrıca kendi mümessillerini
   * görebilecek»). Tek satır: «Ekibim» (`/team`). Çalışma alanında DEĞİL — o bölüm şimdilik yalnız sistem
   * yöneticisinde (aynı gün kararı) ve bölge müdürünün menüsünde onu açmamalı.
   *
   * KAPI `hr.team.read` (BOLGE_MUDURU şablonunda): ekibi organizasyon şemasından çözülüyor, yani anahtar «bir
   * birimin başıyım» demiyor, «Ekibim ekranını açabilirim» diyor; başı olmadığı bir birimin ekibini hiçbir
   * kimlik girdisiyle isteyemez. `aboutOthers`: ekranın her satırı başka bir kişinin kaydı.
   *
   * «GERİ BİLDİRİMLER» ayrı bir satır değil: yazan yönetici Ekibim'den (ve oradaki bağlantıdan) açar, üst yönetim
   * Panelim'deki «Ekipten geri bildirimler» bölümünden; İK'ya İK bölümünde bir satır var (aşağıda). Kapısı
   * (`hr.team_feedback.read`) EMPLOYEE'de de var — anahtar bir ekran açmıyor, bir sorgunun cevabını belirliyor.
   */
  {
    title: "Ekip",
    panel: "hr",
    aboutOthers: true,
    items: [
      {
        label: "Ekibim",
        to: "/team",
        icon: UsersRound,
        permissions: ["hr.team.read"],
        module: "module.hr",
      },
    ],
  },

  /* ---------------------------------------------------------------- HR panel */

  /* --------------------------------------------------------------- inventory panel */
  /**
   * Stok. Every item is gated on `module.inventory` as well as a permission: a customer who
   * did not buy the module has no stock screens at all, rather than screens that 404.
   *
   * The order is the order of a working day: what things ARE, where they are kept, what moved,
   * and what is there now. Balances last on purpose — it is the answer, and the three above it
   * are how the answer came to be.
   */
  {
    /**
     * «DEPO › STOK» (7 Ekim 2026, ikinci tur): «Stok › Stok» tekrar ediyordu; satırı «Stok
     * Durumu» yapmak da yanıltıcıydı — satır BÜTÜN stok bölümünü açıyor ve Ürünler/Depolar
     * sekmelerindeyken de vurgulu duruyor. Grup yeri söylüyor, satır bölümün adını.
     */
    title: "Depo",
    panel: "inventory",
    aboutOthers: true,
    items: [
      /**
       * ON DOKUZ SATIR DÖRDE İNDİ, ve bu depo müdürünün menüsünün neredeyse tamamıydı.
       *
       * Otuz satırlık bir menüde insan aradığını okuyarak değil HATIRLAYARAK buluyor;
       * hatırlamıyorsa hepsini tek tek geziyor. Yönetim panelinde aynısı yapıldı ve orada
       * işe yaradı: menü BÖLÜME işaret ediyor, sekmeler sayfanın içinde.
       *
       * «İş Ortakları» stok değil — `core.partners`, satış ve satın almanın paylaştığı iş verisi;
       * stokun altına konmadı.
       *
       * MENÜ SADELEŞTİ (1 Kasım 2026, kullanıcı kararı: «Satış ve Depo menüsü sadeleşsin»):
       * «Kullanılabilirlik» artık Stok'un sekmesi (`/inventory/availability`, adres aynı);
       * «Fiyat Listeleri» Mali temel'den buraya, ürünün yanına geldi (İSF listesi, KURAL-14);
       * «Karekod Sorgula» İTS modülüne bağlandı ve İTS'i kapalı kurulumda görünmüyor.
       */
      /**
       * KAPISI `balance.read`, `product.read` DEĞİL — 16 Eylül 2026.
       *
       * ÜRÜN KATALOĞU REFERANS VERİSİ: satış teklifine satır eklerken ürün seçiyor, stok onu
       * yönetiyor. Kapı `inventory.product.read` iken satışa o anahtarı vermenin tek yolu ona
       * STOK BÖLÜMÜNÜ de göstermekti — yani teklife ürün ekleyebilmesi için stokçu olması
       * gerekiyordu. Ölçüldü: `QuotationDrawer` satır eklerken `/inventory/products` çağırıyor
       * ve satış müdürlüğünde o anahtar yoktu.
       *
       * Bölümün kendisi zaten STOK DURUMUYLA açılıyor — ilk sekmesi «Stok Durumu», verisi
       * `inventory.balance.read`. Kapıyı oraya bağlamak bölümü kimseden almıyor (depo müdürü
       * ve müdür ikisini de taşıyor) ama ürün okumasını bölümün kapısı olmaktan çıkarıyor.
       */
      {
        label: "Stok",
        to: "/inventory",
        icon: Boxes,
        permissions: ["inventory.balance.read"],
        module: "module.inventory",
        matchPrefix: true,
      },
      /**
       * FİYAT LİSTELERİ ÜRÜNÜN YANINDA (1 Kasım 2026) — Mali temel'den taşındı: liste ürünün İSF'si
       * (KURAL-14), bir mali tanım değil. Adres aynı (`/finance/price-lists`); okuyan patron ve satış
       * müdürlüğü (`SALES_PRICING`). Modül kapısı yok: tablo Mali temel'in, kurulumdan bağımsız.
       */
      {
        label: "Fiyat Listeleri",
        to: "/finance/price-lists",
        icon: ReceiptText,
        permissions: ["finance.price.read"],
        matchPrefix: true,
      },
      /**
       * Kapısı ÇAĞIRDIĞI uca göre: ekran `/inventory/lookup/gs1` (bakiye) ve
       * `/inventory/serials` (parti) okuyor, ürün kataloğunu HİÇ okumuyor. `product.read`
       * kapısı yanlıştı — ölçüldü, 16 Eylül 2026.
       */
      /**
       * Modülü `pharma.its` (1 Kasım 2026): karekod İTS'in parçası; İTS kapalı kurulumda satır, rota ve uç
       * (`@RequireModule`) birlikte kapanıyor. Anahtar aynı kaldı — rol değişmedi.
       */
      {
        label: "Karekod Sorgula",
        to: "/inventory/lookup",
        icon: ScanLine,
        permissions: ["inventory.balance.read"],
        module: "pharma.its",
      },
    ],
  },

  /* ----------------------------------------------------------- operations panel */
  /**
   * OPERASYON — sevkiyat işleri ve kapı giriş/çıkış (17 Ekim 2026; GECE_RAPORU #29: «Operasyon
   * (sevkiyat, kapı) formları web'de yok»). Uçlar hazırdı; ekran sözleşmesi
   * `docs/design/ekran-sozlesmesi/30-OPERASYON-YONETIM.md` §3–§4.
   *
   * İKİ SATIR, ÇÜNKÜ İKİ AYRI İŞ: planlamacı işi kuruyor ve yönetiyor, kapı görevlisi gelen aracı
   * kaydediyor. Kapıları kendi okuma anahtarları; yazma düğmeleri sayfanın içinde kendi
   * anahtarlarıyla (`manage`, `check_in`, `check_out`, `cancel`) çiziliyor ya da hiç çizilmiyor.
   * Patron okuma anahtarlarını taşıyor: listeyi ve kartı görür, düğme görmez.
   */
  {
    title: "Operasyon",
    panel: "operations",
    aboutOthers: true,
    items: [
      /**
       * SEVKİYAT TAKİBİ (1 Kasım 2026) — «kim getirdi, kaçta indirdi, kaçta götürdü, ne zaman teslim
       * etti». Bölümün ilk satırı: okuyan herkesin sorusu bu (depo, patron, müdür, satış — kullanıcı
       * kararı). Müdür ve satış bu satırı açan üç okumayı taşıyor, kapıyı açanı taşımıyor.
       */
      {
        label: "Sevkiyat Takibi",
        to: "/operations/shipment-tracking",
        icon: PackageSearch,
        permissions: ["operations.transport_job.read"],
        module: "module.operations",
      },
      /**
       * PLANLAMA EKRANI, EKRANIN İSTEDİĞİ DÖRT ANAHTARLA (1 Kasım 2026). Form tesis, araç ve sürücü
       * seçicilerini taşıyor; `check:screen-access` müdürde ve satışta (Sevkiyat Takibi'ni okuyanlar)
       * araç, sürücü ve tesis okumasını EKSİK buldu — satır menüdeydi ama ekran o uçlara giremiyordu.
       * Kullanıcının kararı onlara araç ve sürücü kartını AÇMAMAK; bu yüzden anahtar verilmedi, satır
       * ekranı açabilene kısıldı (kit `permissions`'ı HEPSİ okuyor, rota da aynısını soruyor). Depo ve
       * patron dördünü de taşıyor, onlarda değişen yok; okuyan herkes işi «Sevkiyat Takibi»nde görüyor.
       */
      {
        label: "Sevkiyat",
        to: "/operations/transport-jobs",
        icon: Truck,
        permissions: [
          "operations.transport_job.read",
          "organization.site.read",
          "operations.vehicle.read",
          "operations.driver.read",
        ],
        module: "module.operations",
      },
      {
        label: "Kapı",
        to: "/operations/gate-visits",
        icon: DoorOpen,
        permissions: ["operations.gate_visit.read"],
        module: "module.operations",
      },
      /**
       * SÜRÜCÜLER (1 Kasım 2026) — kendi şoförlerimiz ve taşımacıların şoförleri; şoför hesabı buradan açılır
       * (kullanıcı kararı: giriş davet + e-posta/parola, şoför yalnız kendi seferlerini görür).
       */
      {
        label: "Sürücüler",
        to: "/operations/drivers",
        icon: IdCard,
        permissions: ["operations.driver.read"],
        module: "module.operations",
      },
    ],
  },

  /* ---------------------------------------------------------------- analiz panel */
  /**
   * ANALİZ (A2, 1 Kasım 2026; `docs/plan/ANALIZ_PLANI.md` §3.2, KARAR Q2) — TEK SATIR, sekmeli bölüm: genel bakış
   * (yönetim karnesi) ve alan sekmeleri (saha, stok ve sevkiyat, filo, İK). Satır alanlardan BİRİNİ başkaları için
   * okuyana çiziliyor (`aboutOthers` + `anyPermissions`): patron, genel müdür, direktör, bölüm başları, bölge
   * müdürleri. Kendi kaydını okuyan çalışan ve mümessil görmüyor. Hangi sekmenin çizileceğine bölüm, kimin hangi
   * kapsamla sayı gördüğüne sunucu karar veriyor.
   *
   * ANAHTARLAR ELLE YAZILI, sabitten değil: ekran erişim çiti (`check:screen-access`) bu dosyayı METİN olarak okuyor.
   * Liste `src/lib/analytics/analytics-access.ts › ANALYTICS_MENU_KEYS` ile aynı olmalı (`analytics-access.test.ts`).
   */
  {
    title: "Analiz",
    panel: "reports",
    aboutOthers: true,
    items: [
      {
        label: "Analiz",
        to: "/analytics",
        icon: ChartColumnIncreasing,
        anyPermissions: [
          "channel.analysis.read",
          "events.field_analytics.read",
          "events.physician_visit.read",
          "hr.employment.read",
          "hr.leave.read",
          "fleet.cost.read",
          "inventory.balance.read",
          "operations.transport_job.read",
          "events.event.read",
          "workflow.analytics.read",
          "finance.ledger.read",
        ],
        matchPrefix: true,
      },
    ],
  },

  /* ---------------------------------------------------------------- admin panel */
  /**
   * Manage, not read. Reading the organisation is an HR-panel concern — "Organizasyon Şeması"
   * lives there — and every HR role holds `organization.*.read` so an employee record can name
   * its unit. Gated on read, these six screens put a "Yönetim" tab in front of every HR
   * specialist, warehouse manager and auditor, for pages they could only look at. The admin
   * panel is for changing the organisation; that is what `manage` means.
   */
  /**
   * YÖNETİM TEK BÖLÜM, dört değil.
   *
   * Önce otuz menü satırı dört sekmeli sayfaya indirildi; ama menü sekmelerin KENDİSİNİ
   * listelemeye devam ediyordu, yani kazanç yalnız sayfada kaldı — kenar çubuğunda hâlâ on iki
   * satır ve dört başlık vardı. Sekme zaten sayfanın içinde görünüyor; onu bir de menüde
   * göstermek, aynı gezinmeyi iki ayrı yerde öğretmek demek ve ikisi ayrıştığında hangisinin
   * doğru olduğu sorulamaz.
   *
   * Şimdi menü BÖLÜME işaret ediyor, sekmeye değil: dört satır, tek başlık. Sekmeler arası
   * geçiş sayfanın kendi işi.
   *
   * YETKİ LİSTESİ SEKMELERİN BİRLEŞİMİ ve bu, `holdsOne` semantiği yüzünden doğru davranıyor:
   * satır, sekmelerden HERHANGİ birine yetkisi olana görünür. Tek bir yetki yazsaydık —
   * diyelim yalnız `legal_entity.manage` — sadece kadro yetkisi olan biri, girebileceği bir
   * sekmeyi menüde hiç göremezdi. Hangi sekmelerin çizileceğine `AdminSection` kendi içinde
   * karar veriyor ve hiçbir sekmesi kalmayan bölüm zaten bu listeden düşüyor.
   *
   * ESKİ SEKME ADRESLERİ ÇALIŞMAYA DEVAM EDİYOR (`/admin/organization/positions` gibi): router'da
   * `EXTRA_ROUTES` olarak bölümlerine bağlandılar, yani yer imleri kırılmadı ve yetki kapısı
   * aynı kaldı. Menü değişti, adresler değişmedi.
   */
  {
    title: "Yönetim",
    panel: "admin",
    aboutOthers: true,
    items: [
      /**
       * «YÖNETİM PANOSU» SATIRI KALDIRILDI — 16 Eylül 2026, kullanıcı kararı.
       *
       * Uzun süre burada durdu ve favorilere SABİTLENİYORDU; sebebi doğruydu (rayda iki kez
       * görünmesin) ama asıl sorun daha derindeydi: o da bir DEPARTMAN panosuydu — sistem
       * yöneticisinin departmanı — ve ürün artık tek pano istiyor. Sistem yöneticisi için
       * «Panelim»in boş görünmesinin sebebi de buydu: hesabın arkasında personel kaydı yok ve
       * ona ait içerik başka bir ekrandaydı. Şimdi kendi içeriği kendi panelinde
       * (`AdminPanelBlock`) — sayaçlar, ve yönetim ile platform satırlarının kısayolları.
       *
       * `/admin` adresi ve `AdminDashboardPage` ekranı silindi. Bölüm satırları (Kuruluş,
       * Erişim, Sistem, Sunucu, Denetim) olduğu gibi duruyor: kalkan şey pano, panel değil.
       */
      /** Tüzel kişilik, lokasyon, birim ağacı ve kadro — şirketin kendi şekli. */
      {
        label: "Kuruluş",
        to: "/admin/organization",
        icon: Building2,
        anyPermissions: [
          "organization.legal_entity.manage",
          "organization.org_unit.manage",
          "organization.job_title.manage",
        ],
        matchPrefix: true,
      },
      /**
       * GÜVENLİK OLAYLARI DENETİMDE DEĞİL BURADA. Bir oturumun nereden açıldığıyla o oturumda
       * başarısız bir giriş denemesi olduğu aynı sorunun iki yarısı; birini «Erişim»de,
       * öbürünü «Denetim»de aramak, şüphelenen kişiyi iki ekran arasında gezdiriyordu.
       */
      {
        label: "Erişim ve Güvenlik",
        to: "/admin/access",
        icon: Users,
        anyPermissions: [
          "identity.user.read",
          "authorization.role.read",
          "identity.session.read_any",
        ],
        matchPrefix: true,
      },
      /**
       * Anahtar listesi sekmelerin BİRLEŞİMİ — «Denetim» satırındaki kuralın aynısı, çünkü
       * bölümün sekme adresleri kapısını bu satırdan miras alıyor. «Veri Modeli» ile «Otomasyon
       * ve Altyapı» sekmeleri bloklarından BİRİNİ görebilen herkese açılıyor
       * (`SystemSectionPage`), o yüzden blok anahtarları da burada; yalnız
       * `workflow.definition.read` yazılıyken `system.job.read` tutan biri otomasyon sekmesine
       * bu satırdan ulaşamazdı. `nav-catalog.test.ts` iki dosyanın uyuşmasını sınıyor.
       *
       * `templates.template.read` («Belge Şablonları») BİLEREK YOK: o anahtar herkeste
       * (`PERSONAL_WORKSPACE`), burada olsaydı «Sistem» her çalışanın menüsüne girerdi.
       */
      {
        label: "Sistem",
        to: "/admin/system",
        icon: Settings2,
        anyPermissions: [
          "configuration.setting.read",
          "configuration.module.manage",
          "configuration.custom_field.read",
          "configuration.lookup.read",
          "workflow.definition.read",
          "notifications.template.manage",
          "system.job.read",
          "configuration.storage.manage",
          "system.export.run",
        ],
        matchPrefix: true,
      },
      /**
       * SUNUCU DURUMU — ayarın değil, KURULUMUN kendi hâli.
       *
       * `/health` ve `/ready` uçları vardı ama ekranı yoktu: sunucunun ayakta olup olmadığını
       * görmek için terminal ya da ham JSON gerekiyordu. Yöneticinin menüsünde ayarlar vardı,
       * yönettiği şeyin durumu yoktu.
       *
       * `system.job.read` ile kapalı, yeni anahtar açılmadı: ekrandaki her sayı zaten arka plan
       * işlerinin ve kuyruğun hâli — o anahtarı tutan kişi bunları görebiliyordu, yalnız tek tek
       * ve farklı ekranlarda.
       */
      {
        label: "Sunucu",
        to: "/admin/server",
        icon: Server,
        permissions: ["system.job.read"],
      },
      /**
       * KİŞİSEL İÇERİK DENETİMİ VE YASAL SAKLAMA DA BU SATIRIN ALTINDA (K4, K5). Anahtar listesi
       * sekmelerin BİRLEŞİMİ ve öyle KALMALI: bölümün rotaları kapılarını bu satırdan miras
       * alıyor (bkz. `routes/router.tsx`, `under: "/admin/audit"`), yani listede olmayan bir
       * sekme anahtarı, o anahtarı taşıyan kişiyi kendi sekmesinden dışarıda bırakır.
       * `audit.personal_content.read` verilmiş bir kişi (ör. patron) denetim kayıtlarını
       * okuyamıyor olabilir; `system.retention.manage` taşıyan biri de öyle. `holdsOne`
       * semantiği bunu doğru yapıyor, hangi sekmenin çizileceğine `AdminSection` karar veriyor.
       */
      {
        label: "Denetim",
        to: "/admin/audit",
        icon: ScrollText,
        anyPermissions: [
          "audit.log.read",
          "audit.sensitive_access.read",
          "audit.integrity.read",
          "audit.personal_content.read",
          "system.retention.manage",
        ],
        matchPrefix: true,
      },
    ],
  },
  /**
   * «RAPORLAR» GRUBU MENÜDEN KALDIRILDI — şimdilik kapalı.
   *
   * Karar ürün tarafından: rapor yüzeyi henüz kimseye açılmıyor. Ekranlar ve kapıları
   * duruyor, yalnız menüde satırı yok; `EXTRA_ROUTES` ikisini de KENDİ anahtarıyla taşıyor
   * (`reporting.report.read` ve `system.export.run` — ikisi ayrı, ve bu ayrımın bedeli bir
   * kez ödendi: bkz. aynı dosyadaki «Dışa Aktarımlar» yorumu).
   */

  /* ------------------------------------------------------------- platform panel */
  /**
   * The vendor's own screens: who the customers are, what they bought, what they paid.
   *
   * SHARED ONLY, and that is a statement about the AUDIENCE rather than about security. A
   * dedicated installation has one customer — a list of one row answers a question nobody
   * asked — and its administrator is the customer's own IT manager, for whom our commercial
   * layer is not merely useless but none of their business.
   *
   * `aboutOthers: true` is the strongest case of it in the catalogue: every row on these
   * screens is somebody else's company. A `SELF`-scoped grant must never open them, and the
   * router applies the same rule to a hand-typed URL.
   */
  /* ------------------------------------------------------------------- sales panel */
  /**
   * Pazarlama zincirin BAŞI: satış "ne söz verdik"i tutar, pazarlama "kiminle konuşuyoruz"u.
   * Menüde satıştan ÖNCE duruyor ve bu bir sıralama tercihi değil — zincir öyle akıyor, ve
   * menü akışı tersine çevirirse kullanıcı hangisinin hangisinden doğduğunu menüden öğrenemez.
   *
   * ETKİNLİK BU BÖLÜMÜN İÇİNDE (21 Eylül 2026). Ayrı bir «Etkinlik» bölümüydü ve ayrı bir
   * `EVENTS_YONETICI` rolü üretiyordu. Kullanıcı kararı: *«pazarlama → burası önemli, etkinlik
   * vs buraya bağlı, ayrı bir etkinlik rolü kesinlikle olmayacak.»* Kongreyi yürüten kişi
   * pazarlamacı; ayrı bölüm, var olmayan bir masaya başkan atıyordu.
   *
   * LİSANS AYRI KALDI, DEPARTMAN BİRLEŞTİ: `module.events` hâlâ ayrı satın alınabiliyor
   * (kongre isteyip CRM istemeyen müşteri olur) ve satırlar kendi `module` kapılarını
   * taşıyor. Değişen yalnız kimin masası olduğu — `departmentOf: "module.crm"`.
   *
   * KONGRE ÖNCE, CRM SONRA: İntraline'da asıl iş kongre; aday-fırsat hunisi bugün boş ve
   * `module.crm` kapalı bir kurulumda satırlar hiç görünmüyor. Sık kullanılanı üste koymak
   * bir tercih değil, menünün işi.
   *
   * ADAY → FIRSAT → (satışa devreder). Aktiviteler en sonda: ikisine birden bağlanıyor ve tek
   * başına bir adım değil.
   */
  {
    title: "Pazarlama",
    panel: "marketing",
    aboutOthers: true,
    items: [
      {
        label: "Kongre ve Etkinlik",
        to: "/events",
        icon: CalendarDays,
        permissions: ["events.event.read"],
        module: "module.events",
        // Etkinlik kartı (`/events/:id`) kapısını BU satırdan miras alıyor (`under`), yani
        // kapı tek yerde yaşıyor.
        matchPrefix: true,
      },
      {
        label: "Yıllık Plan",
        to: "/events/plans",
        icon: ClipboardList,
        permissions: ["events.event.read"],
        module: "module.events",
      },
      {
        /**
         * AYRI ANAHTAR, bilerek: `events.participant.read`, `events.event.read` değil.
         * Etkinliğin bütçesini görmek, kimin geldiğini görmeyi gerektirmiyor — KVKK'da
         * gerektirmemesi gereken tam olarak bu.
         */
        label: "Hekimler",
        to: "/physicians",
        icon: Stethoscope,
        permissions: ["events.participant.read"],
        module: "module.events",
        matchPrefix: true,
      },
      /**
       * HEKİM GÖRÜŞMELERİ — 28 Ekim 2026: «ilgili mümessil doktorla görüşme yaptıysa kaydı
       * tutulsun, bölge müdürü onaylasın».
       *
       * KAPISI `record`, `read` DEĞİL: satırı görmesi gereken ilk kişi kaydı YAZAN mümessil ve
       * onda okuma anahtarı yok. Listenin kime ne göstereceğini sunucu ayırıyor.
       */
      {
        label: "Hekim Görüşmeleri",
        to: "/physician-visits",
        icon: ClipboardList,
        permissions: ["events.physician_visit.record"],
        module: "module.events",
        matchPrefix: true,
      },
      /**
       * SUNUM TALEPLERİ (KİŞİSEL) — 30 Ekim 2026, kullanıcı: «sunum talepleri kısmı soldaki
       * sidebarda da olsun, sunum talebi diye, mümessillerde ve bölge müdüründe». Talebi AÇANIN
       * sayfası: kendi talepleri ve — ekibi olan için — ekibinden onayına gelenler. Pazarlamanın
       * bütün talepler satırını gören bu satırı görmüyor (`unlessPermissions`): aynı adlı iki satır
       * olurdu ve pazarlamanın listesi kendi taleplerini de içeriyor.
       */
      {
        label: "Sunum Talepleri",
        to: "/my-presentation-requests",
        icon: Presentation,
        permissions: ["events.presentation.request"],
        unlessPermissions: ["events.presentation.read"],
        /* Grup «başkalarının kaydı»; bu satır kişinin KENDİ talepleri — mümessilin anahtarı SELF. */
        ownRecords: true,
        module: "module.events",
      },
      /**
       * SUNUM TALEPLERİ — pz-akis 6 Ekim 2026 (plan M15). Tıbbi tanıtım temsilcisinin bir
       * kuruma ya da hekime sunum isteği: bölge müdürü → pazarlama onayı.
       *
       * Bu satır PAZARLAMANIN listesi (`events.presentation.read`, başkalarının talepleri).
       * Temsilci ve bölge müdürü kendi sayfalarını yukarıdaki satırdan açıyor
       * (`/my-presentation-requests`, 30 Ekim 2026'ya kadar yalnız Panelim kutusundan). Üç kapı,
       * üç soru: «hepsi», «benimkiler», «önüme gelen».
       */
      {
        label: "Sunum Talepleri",
        to: "/presentation-requests",
        icon: Presentation,
        permissions: ["events.presentation.read"],
        module: "module.events",
      },
      /**
       * ÜRÜN TALEPLERİ — pz-akis 6 Ekim 2026 (plan M12). Yeni ürün kartı pazarlamada doğuyor,
       * ruhsat ve depodan geçip pazarlamanın son onayıyla kataloğa giriyor.
       *
       * Kart sayfası (`/product-requests/:id`) kapısını bu satırdan MİRAS ALMIYOR: ruhsat ve
       * depo bu satırı görmeden talebe onay kutusundan ulaşıyor, o yüzden kartın kendi
       * «herhangi biri yeterli» kapısı var (`router.tsx`). `matchPrefix` yalnız vurgu için.
       */
      {
        label: "Ürün Talepleri",
        to: "/product-requests",
        icon: PackagePlus,
        permissions: ["marketing.product_request.read"],
        module: "module.crm",
        matchPrefix: true,
      },

    ],
  },

  /**
   * FİLO — şirket içi araçlar (6 Ekim 2026; docs/plan/FILO_PLANI.md §3.2). Altı satır: genel bakış,
   * araçlar (araç kartı `matchPrefix`), zimmet, bakım ve hasar, ceza ve geçişler, masraflar. Satırlar
   * başkasının kaydını gösteriyor (`aboutOthers: true`); çalışanın kendi aracı Panelim'de.
   */
  {
    title: "Filo",
    panel: "fleet",
    aboutOthers: true,
    items: [
      { label: "Genel Bakış", to: "/fleet", icon: LayoutDashboard, permissions: ["fleet.vehicle.read"], module: "module.fleet" },
      { label: "Araçlar", to: "/fleet/vehicles", icon: CarFront, permissions: ["fleet.vehicle.read"], module: "module.fleet", matchPrefix: true },
      { label: "Zimmet", to: "/fleet/assignments", icon: KeyRound, permissions: ["fleet.assignment.read"], module: "module.fleet", matchPrefix: true },
      { label: "Bakım ve Hasar", to: "/fleet/maintenance", icon: Wrench, permissions: ["fleet.maintenance.read"], module: "module.fleet" },
      { label: "Ceza ve Geçişler", to: "/fleet/fines", icon: Receipt, permissions: ["fleet.fine.read"], module: "module.fleet" },
      { label: "Masraflar", to: "/fleet/costs", icon: Wallet, permissions: ["fleet.cost.read"], module: "module.fleet" },
    ],
    // `items`'tan SONRA: `check-sidebar` ve `check:screen-access` grubun başını kalıpla okuyor.
    companyLevel: true,
  },

  /**
   * Satış zinciri. `module.sales` kapılı: satmayan bir kurulum da var — tek bir müşteriye
   * üretim yapan fason bir tesis gibi.
   *
   * TEKLİF ÖNCE, SİPARİŞ SONRA ve bu bir sıralama tercihi değil: zincir öyle akıyor ve menü
   * akışı tersine çevirirse kullanıcı hangisinin hangisinden doğduğunu menüden öğrenemez.
   */
  {
    title: "Satış",
    panel: "sales",
    aboutOthers: true,
    items: [
      /**
       * TEKLİF VE SİPARİŞ GERİ — 17 Ekim 2026 (kullanıcı talimatı). 21 Eylül'de «notlarda yok,
       * kaydı 0» diye çıkmışlardı (`b7f42d6c`); kod duruyordu. Kapı `module.sales`: satmayan
       * bir kurulumda iki satır da yok.
       */
      {
        label: "Teklifler",
        to: "/sales/quotations",
        icon: FileText,
        permissions: ["sales.quotation.read"],
        module: "module.sales",
        matchPrefix: true,
      },
      {
        label: "Siparişler",
        to: "/sales/orders",
        icon: ShoppingCart,
        permissions: ["sales.order.read"],
        module: "module.sales",
        matchPrefix: true,
      },
      /**
       * MÜŞTERİLER — satis-duzeltme 20 Ekim 2026 (19 Ekim denetimi S-26). Satış müdürünün ana
       * çalışma verisi (cari kart) yalnız rayın altındaki «Şirket › İş Ortakları» sekmesindeydi:
       * üç tık ve gizli. Aynı ekran, alıcılara süzülmüş (`CustomersPage`). Kapı HEPSİ: kartı
       * okumak VE satışın teklif anahtarı — kartı okuyan depo bu satırı görmüyor, onun yeri
       * yine «Şirket».
       *
       * MODÜLÜ `core.partners` (1 Kasım 2026): teklif ve sipariş kurulumda kapanınca (`module.sales`)
       * müşteri kartı kalıyor — ecza depoları da birer müşteri ve Kanal onlara bağlı. Teklif anahtarı
       * burada «satış kişisi» işareti; modül kapalıyken de rolde duruyor, ön yüzün `can()`'ı modüle bakmıyor.
       */
      {
        label: "Müşteriler",
        to: "/sales/customers",
        icon: BookUser,
        permissions: ["partners.partner.read", "sales.quotation.read"],
        module: "core.partners",
        matchPrefix: true,
      },
      /**
       * KANAL — ecza deposu zincirinin tamamı, TEK satır.
       *
       * İki satırdı: «Kanal raporları» ve «Kanal verisi». Gerekçesi «iki farklı iş» idi —
       * biri ayda bir dosya getiriyor, öteki her gün sayıya bakıyor. Doğruydu ama menüde
       * çözülmesi gereken bir sorun değildi: ikisi de AYNI zincirin adımları ve ayrı
       * satırken aradaki sıra görünmüyordu. Verinin neden eksik olduğunu anlamak için öbür
       * satıra geçmek, geçince de ilk sorunun ne olduğunu unutmak gerekiyordu.
       *
       * 31 Ekim 2026'dan beri altı sekme (ticari veri): Özet → Satış analizi → Mutabakat →
       * Yüklemeler → Kurallar → Eczane Havuzu. Eski adresler yönleniyor: «Veri»
       * (`/sales/channel/facts`) özete, «Ecza Depoları» (`/sales/channel/distributors`) kurallara.
       *
       * SATIŞ PANELİNDE, ama veriyi depo da okuyor: `channel.report.read` izni depo
       * çalışanına da verilebilir. Menüdeki yeri, iznin kimde olabileceğini daraltmıyor.
       *
       * Teklif ve siparişten SONRA: onlar bizim verdiğimiz söz, bu dışarıdan gelen ölçüm.
       */
      {
        label: "Kanal",
        to: "/sales/channel",
        icon: FileSpreadsheet,
        permissions: ["channel.report.read"],
        /**
         * KAPI `pharma.ims`, `module.sales` DEĞİL — 16 Eylül 2026.
         *
         * Satır satış panelinde duruyor ve durmalı; oradaki insanın işi. Ama ekranın
         * ARKASINDAKİ yetenek sektöre ait: ecza deposu, GLN, brick. `module.sales`'e
         * bağlıyken ilaç dışı bir müşteri «Satış»ı açtığında bu satırı görüyordu.
         *
         * Panelin yeri ile modülün kimliği ayrı şeyler; bu satır ikisinin ayrıldığı yer.
         */
        module: "pharma.ims",
        matchPrefix: true,
      },
    ],
  },

  /* ----------------------------------------------------------------- finance panel */
  /**
   * MALİ İŞLER (1 Kasım 2026, finans F1 — eski adı «Mali temel»): yönetim defteri, Finans Özeti, kategoriler;
   * vergi ve kur. Satışın altında DEĞİL, kendi panelinde — alış modülü geldiğinde aynı vergi ve kur tablosunu
   * okuyacak, ve KDV tanımını "satış"ın içine koymak o günü bir taşıma işine çevirirdi.
   *
   * Vergi Sınıfları'nın `module` kapısı YOK: vergi her kurulumda gerekli. Yönetim defteri, kategoriler ve Döviz
   * Kurları «Finans ve muhasebe» (`module.finance`) ile açılıyor — kur artık satışın değil, defterin TL karşılığının
   * da kaynağı (TCMB günlük kur işi).
   */
  {
    title: "Mali İşler",
    panel: "finance",
    // Kendi kaydın hakkında değil, işletmenin hakkında: bir fiyat listesi kimsenin kişisel
    // kaydı değildir ve bu panelin her satırı `requireAnyRecord` kapısından geçer.
    aboutOthers: true,
    items: [
      /**
       * YÖNETİM DEFTERİ (finans F1, `docs/plan/FINANS_MUHASEBE_PLANI.md` §7): Finans Özeti patron ve Mali İşler'in
       * ilk ekranı; Gider Defteri satır satır kaynağıyla; Kategoriler kategori ağacı ve eşleme (yönetim anahtarı).
       * `/finance` önek eşlemesiz: alt ekranlar kendi satırlarında.
       */
      { label: "Finans Özeti", to: "/finance", icon: PieChart, permissions: ["finance.ledger.read"], module: "module.finance" },
      { label: "Gider Defteri", to: "/finance/ledger", icon: BookOpenText, permissions: ["finance.ledger.read"], module: "module.finance" },
      { label: "Gelir ve Gider", to: "/finance/income-expense", icon: ChartColumnBig, permissions: ["finance.ledger.read"], module: "module.finance" },
      /**
       * Bütçe ve raporlar (finans F5). Raporların her sekmesi kendi anahtarıyla: biri yeter, görmediği sekme ekranda yok.
       */
      { label: "Bütçe", to: "/finance/budgets", icon: Target, permissions: ["finance.budget.read"], module: "module.finance" },
      { label: "Mali Raporlar", to: "/finance/reports", icon: FileChartColumn, anyPermissions: ["finance.gl.read", "finance.ledger.read", "finance.expense.read"], module: "module.finance" },
      /**
       * MASRAF VE ÖDEME (finans F2c-2, 2 Kasım 2026; plan §7). Kontrol kuyruğunun tek beyan sayfası `EXTRA_ROUTES`'ta,
       * satırın kapısıyla. Ödenecekler iki anahtarla açılıyor: İK «Bordroya eklenecekler»i dar `finance.payroll_queue.read` ile
       * okur (ödenecekleri ve beyanları görmez; sayfa ona yalnız o sekmeyi açıyor, 3 Kasım 2026).
       */
      { label: "Masraf Kontrolü", to: "/finance/expense-review", icon: ClipboardCheck, permissions: ["finance.expense.review"], module: "module.finance", matchPrefix: true },
      { label: "Masraf Beyanları", to: "/finance/expense-claims", icon: ReceiptText, permissions: ["finance.expense.read"], module: "module.finance" },
      { label: "Ödenecekler", to: "/finance/payables", icon: HandCoins, anyPermissions: ["finance.payable.read", "finance.payroll_queue.read"], module: "module.finance" },
      { label: "Ödemeler", to: "/finance/payments", icon: Banknote, permissions: ["finance.payable.read"], module: "module.finance" },
      { label: "Gelen Faturalar", to: "/finance/incoming-invoices", icon: FileInput, permissions: ["finance.invoice.read"], module: "module.finance" },
      { label: "Sabit Kıymetler", to: "/finance/fixed-assets", icon: Boxes, permissions: ["finance.asset.read"], module: "module.finance" },
      { label: "Çalışandan Alacaklar", to: "/finance/receivables", icon: HandCoins, permissions: ["finance.receivable.read"], module: "module.finance" },
      /** Banka ve kart (finans F6): hesaplar, ekstre, eşleştirme, cetvel. */
      { label: "Banka Hesapları", to: "/finance/bank-accounts", icon: Landmark, permissions: ["finance.bank.read"], module: "module.finance" },
      { label: "Banka Mutabakatı", to: "/finance/bank-reconciliation", icon: Scale, permissions: ["finance.bank.read"], module: "module.finance" },
      /**
       * GENEL MUHASEBE (finans F3, 2 Kasım 2026; plan §7). Fişin, ekstrenin ve kuralların alt sayfaları `EXTRA_ROUTES`'ta,
       * satırın kapısıyla. Yevmiye satırı önek eşlemeli: tek fiş ve elle fiş de bu satırda seçili.
       */
      { label: "Yevmiye Fişleri", to: "/finance/journal", icon: BookText, permissions: ["finance.gl.read"], module: "module.finance", matchPrefix: true },
      { label: "Mizan", to: "/finance/trial-balance", icon: Scale, permissions: ["finance.gl.read"], module: "module.finance" },
      { label: "Hesap Planı", to: "/finance/accounts", icon: ListTree, permissions: ["finance.gl.read"], module: "module.finance", matchPrefix: true },
      { label: "KDV Hazırlık", to: "/finance/vat-report", icon: Calculator, permissions: ["finance.gl.read"], module: "module.finance" },
      { label: "Muhasebeleştirme Kuralları", to: "/finance/posting-rules", icon: Workflow, permissions: ["finance.gl.read"], module: "module.finance" },
      { label: "İçe ve Dışa Aktarım", to: "/finance/sync", icon: ArrowLeftRight, permissions: ["finance.sync.run"], module: "module.finance" },
      /**
       * Otomatik eşitleme (finans F4): farklar ve nedenleri, dış sistem bağlantısı. Bağlantıyı yazmak `finance.connector.manage` (CRITICAL,
       * adıyla); ekranı görmek ve «Şimdi eşitle» `finance.sync.run`.
       */
      { label: "Mutabakat", to: "/finance/reconciliation", icon: GitCompareArrows, permissions: ["finance.sync.run"], module: "module.finance" },
      { label: "Bağlantılar", to: "/finance/connectors", icon: Cable, permissions: ["finance.sync.run"], module: "module.finance" },
      { label: "Dönemler", to: "/finance/periods", icon: CalendarCheck, permissions: ["finance.period.manage"], module: "module.finance" },
      { label: "Kategoriler", to: "/finance/categories", icon: FolderTree, permissions: ["finance.category.manage"], module: "module.finance" },
      { label: "Masraf Onay Zinciri", to: "/finance/settings/approval-flow", icon: ListOrdered, permissions: ["finance.approval_flow.manage"], module: "module.finance" },
      // Fiyat Listeleri 1 Kasım 2026'da Depo grubuna, ürünün yanına taşındı.
      {
        /**
         * KAPISI `manage`, `read` DEĞİL — 16 Eylül 2026.
         *
         * Vergi sınıfı listesi bir REFERANS tablosu: ürün formu onu okuyor, teklif onu okuyor.
         * Okuma anahtarını depo müdürüne vermek gerekiyordu (ürün formunda vergi sınıfı
         * seçilemiyordu) ama o anahtar MALİ TEMEL bölümünü açan kapıydı — depo müdürüne mali
         * bölüm göstermeden ürün formunu çalıştırmanın yolu yoktu.
         *
         * Ayrım şu: tabloyu OKUMAK başka alanların formlarının işi, tabloyu YÖNETMEK mali
         * işlerin. Ekran ikincisi için var. Patron okuma anahtarını taşıyor ama yönetmiyor,
         * dolayısıyla bu satırı görmüyor; vergi sınıfını yine her ürünün üstünde görüyor.
         */
        label: "Vergi Sınıfları",
        to: "/finance/tax-categories",
        icon: Percent,
        permissions: ["finance.tax.manage"],
        matchPrefix: true,
      },
      /**
       * Dövizli belgenin donan kurunun ve yönetim defterinin TL karşılığının kaynağı. Kur girmek ayrı anahtar.
       * Modülü `module.finance` (1 Kasım 2026, finans F1): satış kapalı kurulumda da finans kuru görüyor; satır,
       * rota ve uç (`@RequireModule`) birlikte açılıp kapanıyor.
       */
      {
        label: "Döviz Kurları",
        to: "/finance/exchange-rates",
        icon: BadgePercent,
        permissions: ["finance.rate.read"],
        module: "module.finance",
        matchPrefix: true,
      },
    ],
  },

  {
    title: "Platform",
    panel: "platform",
    aboutOthers: true,
    installation: "SHARED",
    items: [
      {
        label: "Müşteriler",
        to: "/platform/tenants",
        icon: Building2,
        permissions: ["platform.tenant.read"],
        matchPrefix: true,
      },
      {
        label: "Paketler",
        to: "/platform/plans",
        icon: Package,
        permissions: ["platform.billing.read"],
        matchPrefix: true,
      },
      {
        label: "Abonelikler",
        to: "/platform/subscriptions",
        icon: ScrollText,
        permissions: ["platform.billing.read"],
        matchPrefix: true,
      },
    ],
  },

];

/**
 * The catalogue as THIS installation sees it.
 *
 * Two frozen arrays computed once, not a filter per call: the result is handed to
 * `buildSidebarGroups` inside a `useMemo`, and returning a fresh array each render would
 * rebuild every group object and defeat the identity checks downstream.
 */
const DEDICATED_CATALOG: readonly AppNavGroup[] = NAV_CATALOG.filter(
  (group) => group.installation !== "SHARED",
);

/**
 * «Herhangi biri yeterli» satırlarını ÇÖZER ve kitin anlayacağı katalogu döndürür.
 *
 * Kit `permissions`'ı HEPSİ diye okuyor — iyi bir varsayılan, ama sekmeli bölüm satırı onun
 * istisnası: «Sistem» dört sekme taşıyor ve canlıdaki «Müdür» rolü yalnız birinin anahtarını
 * tutuyor. HEPSİ ile elenirse o rol, girebileceği sekmeyi menüde hiç göremez.
 *
 * SAF, çünkü bileşenin içinde kalsaydı sınanamazdı: burada sınanan şey bir çizim değil, kimin
 * neyi GÖRDÜĞÜ — ve o soru sessizce yanlış cevaplanabilecek türden.
 *
 * `aboutOthers`, kitin kendi elemesinde kullandığı yüklemin aynısını seçiyor: başkalarının
 * kaydını açan bir satır için `canForOthers` sorulur. İkisi ayrışsaydı menü, rotanın açmayacağı
 * bir satırı gösterebilirdi.
 */
export function resolveAnyPermissions(catalog: readonly AppNavGroup[], holder: NavHolder): readonly AppNavGroup[] {
  return catalog.map((group) => {
    const holds = group.aboutOthers ? holder.canForOthers : holder.can;
    const reachesCompany = (key: string) => {
      const grant = group.companyLevel ? holder.scopeOf?.(key as never) : null;
      return !grant || grant.instanceWide || grant.legalEntityIds.length > 0;
    };
    return {
      ...group,
      items: group.items.flatMap(({ anyPermissions, unlessPermissions, ownRecords, ...item }) => {
        if (!(item.permissions ?? []).every(reachesCompany)) return [];
        if (anyPermissions && !anyPermissions.some((key) => holds(key))) return [];
        if (unlessPermissions?.some((key) => holder.can(key))) return [];
        if (ownRecords) {
          if (!(item.permissions ?? []).every((key) => holder.can(key))) return [];
          const { permissions: _resolvedHere, ...rest } = item;
          return [rest];
        }
        return [item];
      }),
    };
  });
}

/**
 * Bu kurulumun katalogu, «herhangi biri yeterli» satırları ÇÖZÜLMÜŞ hâlde.
 *
 * HOLDER ZORUNLU, ve bu bir kolaylık değil güvenlik: kit `anyPermissions` alanını TANIMIYOR,
 * yani ham katalogu doğrudan `visibleNav`'a veren biri o satırları yetkisiz kişiye de gösterir
 * — filtreden geçmiş gibi görünen, aslında hiç süzülmemiş bir menü. Tek giriş noktasının
 * yetkiyi istemesi, o yolu kapatıyor.
 *
 * Rota tablosu ayrı: `NAV_ITEMS` ham katalogdan kuruluyor ve orada oturum YOK, olamaz da.
 * Kapıyı `gateFor` uyguluyor ve `anyPermissions`'ı `anyOf` olarak `ProtectedRoute`'a geçiriyor,
 * yani elle yazılmış bir adres menüden daha geniş bir cevap alamıyor.
 */
export function navCatalogFor(
  installation: Installation,
  holder: NavHolder,
): readonly AppNavGroup[] {
  return resolveAnyPermissions(installation === "SHARED" ? NAV_CATALOG : DEDICATED_CATALOG, holder);
}
